import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { UserPlus, Copy, Check, Ban, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function InvitePortalDialog({ open, onOpenChange, type, item }) {
  // type: 'family' | 'partner'
  const [form, setForm] = useState({ email: "", full_name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccts, setLoadingAccts] = useState(true);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const entityName = type === "family" ? item?.family_name : item?.name;

  const loadAccounts = async () => {
    setLoadingAccts(true);
    try {
      const { data } = await api.get("/portal-accounts", { params: { entity_type: type, entity_id: item.id } });
      setAccounts(data.items || []);
    } catch { /* ignore */ } finally { setLoadingAccts(false); }
  };

  useEffect(() => {
    if (open && item) {
      setForm({ email: "", full_name: entityName || "", password: "" });
      setCreated(null);
      loadAccounts();
    }
    // eslint-disable-next-line
  }, [open, item?.id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const ep = type === "family" ? `/families/${item.id}/invite` : `/partners/${item.id}/invite`;
      const payload = { email: form.email, full_name: form.full_name };
      if (form.password) payload.password = form.password;
      const { data } = await api.post(ep, payload);
      setCreated({ email: data.account.email, password: data.password });
      toast.success("Portal access created");
      setForm({ email: "", full_name: entityName || "", password: "" });
      await loadAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create portal access");
    } finally { setSubmitting(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/portal-accounts/${id}`); toast.success("Access revoked"); loadAccounts(); }
    catch { toast.error("Could not revoke"); }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(`Login: ${created.email}\nPassword: ${created.password}\nURL: ${window.location.origin}/login`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="invite-portal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <UserPlus size={20} className="text-[#1C4358]" /> Invite to {type === "family" ? "Family" : "Partner"} Portal
          </DialogTitle>
        </DialogHeader>

        {created ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-[#E6EDF2] bg-[#F7FAFC] p-4">
              <div className="flex items-center gap-2 text-[#2F7D5A] font-semibold mb-2"><KeyRound size={16} /> Portal access created</div>
              <div className="text-sm space-y-1">
                <div><span className="text-muted-foreground">Login:</span> <span className="font-medium">{created.email}</span></div>
                <div><span className="text-muted-foreground">Password:</span> <code className="font-medium">{created.password}</code></div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Share these credentials securely. They can sign in at the same login screen.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyCreds} className="flex-1" data-testid="copy-creds-button">
                {copied ? <Check size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />} {copied ? "Copied" : "Copy credentials"}
              </Button>
              <Button onClick={() => setCreated(null)} className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }}>Add another</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="mb-1.5 block text-[13px] font-medium">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required data-testid="invite-email" />
            </div>
            <div>
              <Label className="mb-1.5 block text-[13px] font-medium">Name</Label>
              <Input value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} data-testid="invite-name" />
            </div>
            <div>
              <Label className="mb-1.5 block text-[13px] font-medium">Password (optional — auto-generated if blank)</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder="Leave blank to auto-generate" data-testid="invite-password" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="invite-submit-button">
              {submitting ? "Creating..." : "Create portal access"}
            </Button>
          </form>
        )}

        <div className="pt-2 border-t border-[#E6EDF2]">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Existing portal accounts</div>
          {loadingAccts ? <Skeleton className="h-10" /> : accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">None yet.</p>
          ) : (
            <div className="space-y-2">
              {accounts.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 text-sm rounded-lg border border-[#E6EDF2] px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.email}</div>
                    <div className="text-xs text-muted-foreground">{a.status === "active" ? "Active" : "Revoked"}</div>
                  </div>
                  {a.status === "active" && (
                    <button onClick={() => revoke(a.id)} className="text-[#9C1E22] p-1.5 rounded-lg hover:bg-[rgba(156,30,34,0.08)]" title="Revoke" data-testid="revoke-portal-button"><Ban size={15} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
